import React, { useState } from 'react'
import { Upload, Palette, Save, Check, AlertCircle, Plus, X, Globe } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'

const ClientBrand = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [selectedColors, setSelectedColors] = useState([])
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [faviconFile, setFaviconFile] = useState(null)
  const [faviconPreview, setFaviconPreview] = useState(null)
  const [customColor, setCustomColor] = useState('#000000')
  const [showCustomColorInput, setShowCustomColorInput] = useState(false)

  // Fetch client profile
  const { data: profile, isLoading } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(res => res.data)
  )

  // Upload logo mutation
  const uploadLogoMutation = useMutation(
    async (formData) => {
      const response = await apiService.post('/clients/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Logo uploaded successfully!')
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
        setLogoFile(null)
        setLogoPreview(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to upload logo')
      }
    }
  )

  // Upload favicon mutation
  const uploadFaviconMutation = useMutation(
    async (formData) => {
      const response = await apiService.post('/clients/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Favicon uploaded successfully!')
        queryClient.invalidateQueries('client-profile')
        setFaviconFile(null)
        setFaviconPreview(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to upload favicon')
      }
    }
  )

  // Update brand colors mutation
  const updateBrandMutation = useMutation(
    async (brandData) => {
      const response = await apiService.put('/clients/profile', brandData)
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Brand colors saved successfully!')
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to save brand colors')
      }
    }
  )

  const predefinedColors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6B7280'
  ]

  const handleLogoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setLogoFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setLogoPreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleLogoUpload = () => {
    if (!logoFile) return
    const formData = new FormData()
    formData.append('logo', logoFile)
    uploadLogoMutation.mutate(formData)
  }

  const handleFaviconChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFaviconFile(file)
      const reader = new FileReader()
      reader.onload = (e) => setFaviconPreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const handleFaviconUpload = () => {
    if (!faviconFile) return
    const formData = new FormData()
    formData.append('favicon', faviconFile)
    uploadFaviconMutation.mutate(formData)
  }

  const handleColorToggle = (color) => {
    setSelectedColors(prev => 
      prev.includes(color) 
        ? prev.filter(c => c !== color)
        : [...prev, color]
    )
  }

  const handleAddCustomColor = () => {
    if (customColor && !selectedColors.includes(customColor)) {
      setSelectedColors(prev => [...prev, customColor])
      setShowCustomColorInput(false)
      setCustomColor('#000000')
    }
  }

  const handleRemoveColor = (colorToRemove) => {
    setSelectedColors(prev => prev.filter(color => color !== colorToRemove))
  }

  const isValidHexColor = (color) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)
  }

  const handleSaveColors = () => {
    updateBrandMutation.mutate({ brand_colors: selectedColors })
  }

  React.useEffect(() => {
    if (profile?.brand_colors) {
      setSelectedColors(profile.brand_colors)
    }
  }, [profile])

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" text="Loading brand settings..." />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
          Brand Settings
        </h1>
        <p className="text-neutral-600 dark:text-neutral-400">
          Manage your brand colors and logo. Your agency uses these as a reference while designing your websites.
        </p>
      </div>

      {/* Logo Section */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Upload className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            <span>Logo</span>
            {profile?.logo && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-success-100 dark:bg-success-900/20 text-success-800 dark:text-success-200 rounded-full text-xs">
                <Check className="w-3 h-3" />
                <span>Uploaded</span>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Current Logo */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Current Logo
              </label>
              <div className="w-32 h-32 border-2 border-dashed border-neutral-200 dark:border-[#2f2b41] rounded-lg flex items-center justify-center bg-neutral-50 dark:bg-[#201d2e]">
                {profile?.logo ? (
                  <img 
                    src={profile.logo} 
                    alt="Company Logo" 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="text-center">
                    <Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">No logo uploaded</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload New Logo */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Upload New Logo
              </label>
              <div className="space-y-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="block w-full text-sm text-neutral-500 dark:text-neutral-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-medium
                    file:bg-accent-50 file:text-accent-700 dark:file:bg-accent-900/20 dark:file:text-accent-300
                    hover:file:bg-accent-100 dark:hover:file:bg-accent-900/30"
                />
                
                {logoPreview && (
                  <div className="w-32 h-32 border-2 border-neutral-200 dark:border-[#2f2b41] rounded-lg overflow-hidden">
                    <img 
                      src={logoPreview} 
                      alt="Preview" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                
                <Button
                  onClick={handleLogoUpload}
                  disabled={!logoFile || uploadLogoMutation.isLoading}
                >
                  {uploadLogoMutation.isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Logo
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Favicon Section */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Globe className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            <span>Favicon</span>
            {profile?.favicon && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-success-100 dark:bg-success-900/20 text-success-800 dark:text-success-200 rounded-full text-xs">
                <Check className="w-3 h-3" />
                <span>Uploaded</span>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
            The favicon appears in the browser tab when visitors view your website. Use a square .ico, .png, or .svg file (recommended: 32×32px).
          </p>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Current Favicon */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Current Favicon
              </label>
              <div className="w-20 h-20 border-2 border-dashed border-neutral-200 dark:border-[#2f2b41] rounded-lg flex items-center justify-center bg-neutral-50 dark:bg-[#201d2e]">
                {profile?.favicon ? (
                  <img
                    src={profile.favicon}
                    alt="Favicon"
                    className="w-10 h-10 object-contain"
                  />
                ) : (
                  <div className="text-center">
                    <Globe className="w-6 h-6 text-neutral-400 mx-auto mb-1" />
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">None</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload New Favicon */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Upload New Favicon
              </label>
              <div className="space-y-4">
                <input
                  type="file"
                  accept=".ico,.png,.svg,image/x-icon,image/png,image/svg+xml"
                  onChange={handleFaviconChange}
                  className="block w-full text-sm text-neutral-500 dark:text-neutral-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-medium
                    file:bg-accent-50 file:text-accent-700 dark:file:bg-accent-900/20 dark:file:text-accent-300
                    hover:file:bg-accent-100 dark:hover:file:bg-accent-900/30"
                />
                {faviconPreview && (
                  <div className="w-20 h-20 border-2 border-neutral-200 dark:border-[#2f2b41] rounded-lg overflow-hidden flex items-center justify-center bg-neutral-50 dark:bg-[#201d2e]">
                    <img
                      src={faviconPreview}
                      alt="Preview"
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                )}
                <Button
                  onClick={handleFaviconUpload}
                  disabled={!faviconFile || uploadFaviconMutation.isLoading}
                >
                  {uploadFaviconMutation.isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Favicon
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brand Colors Section */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Palette className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            <span>Brand Colors</span>
            {profile?.brand_colors?.length > 0 && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-success-100 dark:bg-success-900/20 text-success-800 dark:text-success-200 rounded-full text-xs">
                <Check className="w-3 h-3" />
                <span>Set</span>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                Choose Your Brand Colors
              </label>
              <div className="grid grid-cols-5 md:grid-cols-10 gap-3">
                {predefinedColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleColorToggle(color)}
                    className={cn(
                      'w-12 h-12 rounded-lg border-2 transition-all duration-200',
                      selectedColors.includes(color)
                        ? 'border-neutral-900 dark:border-neutral-100 scale-110'
                        : 'border-neutral-200 dark:border-[#2f2b41] hover:scale-105'
                    )}
                    style={{ backgroundColor: color }}
                    title={color}
                  >
                    {selectedColors.includes(color) && (
                      <Check className="w-6 h-6 text-white mx-auto" />
                    )}
                  </button>
                ))}
                
                {/* Add Custom Color Button */}
                <button
                  onClick={() => setShowCustomColorInput(true)}
                  className="w-12 h-12 rounded-lg border-2 border-dashed border-neutral-200 dark:border-[#2f2b41] hover:border-neutral-400 dark:hover:border-neutral-500 transition-colors flex items-center justify-center bg-neutral-50 dark:bg-[#201d2e] hover:bg-neutral-100 dark:hover:bg-[#2f2b41]"
                  title="Add custom color"
                >
                  <Plus className="w-6 h-6 text-neutral-400" />
                </button>
              </div>
            </div>

            {/* Custom Color Input */}
            {showCustomColorInput && (
              <div className="bg-neutral-50 dark:bg-[#201d2e] rounded-lg p-4 border border-neutral-200 dark:border-[#2f2b41]">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Add Custom Color
                </label>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      className="w-12 h-12 rounded-lg border border-neutral-200 dark:border-[#2f2b41] cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      placeholder="#000000"
                      className="w-24 px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-md text-sm font-mono bg-white dark:bg-[#221f2f] dark:text-neutral-100"
                      pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                    />
                  </div>
                  <Button
                    onClick={handleAddCustomColor}
                    disabled={!isValidHexColor(customColor) || selectedColors.includes(customColor)}
                    size="sm"
                  >
                    Add
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCustomColorInput(false)
                      setCustomColor('#000000')
                    }}
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
                {!isValidHexColor(customColor) && customColor !== '#000000' && (
                  <p className="text-xs text-error-500 mt-1">
                    Please enter a valid hex color (e.g., #FF0000 or #F00)
                  </p>
                )}
                {selectedColors.includes(customColor) && isValidHexColor(customColor) && (
                  <p className="text-xs text-warning-500 mt-1">
                    This color is already selected
                  </p>
                )}
              </div>
            )}

            {selectedColors.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Selected Colors ({selectedColors.length})
                </label>
                <div className="flex flex-wrap gap-2">
                  {selectedColors.map((color) => (
                    <div key={color} className="flex items-center space-x-2 px-3 py-1 bg-neutral-100 dark:bg-[#201d2e] rounded-lg group">
                      <div 
                        className="w-4 h-4 rounded-full border border-neutral-200 dark:border-[#2f2b41]"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm text-neutral-700 dark:text-neutral-300">
                        {color}
                      </span>
                      <button
                        onClick={() => handleRemoveColor(color)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-neutral-200 dark:hover:bg-[#2f2b41] rounded"
                        title="Remove color"
                      >
                        <X className="w-3 h-3 text-neutral-500 dark:text-neutral-400" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                onClick={handleSaveColors}
                disabled={selectedColors.length === 0 || updateBrandMutation.isLoading}
              >
                {updateBrandMutation.isLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Colors
                  </>
                )}
              </Button>
            </div>
          </div>

        </CardContent>
      </Card>
    </div>
  )
}

export default ClientBrand
